<template>
<div class="chatMessages">
	<b-navbar class="submenu">
		<b-container>
			<b-navbar-brand href="#">AI Chat Messages</b-navbar-brand>
			<b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
			<b-collapse id="nav-collapse" is-nav>
				<b-navbar-nav>
				</b-navbar-nav>
			</b-collapse>
		</b-container>
	</b-navbar>

	<div class="main-container">
		<div class="content-panel">
			<!-- Loading spinner -->
			<div v-if="isLoading" class="loading-overlay">
				<b-spinner variant="primary" type="grow"></b-spinner>
				<div class="mt-2">Executing query...</div>
			</div>

			<!-- Main content area -->
			<div class="content-wrapper">
				<!-- Filter section -->
				<div>
					<b-form @submit.prevent="applyFilters">
						<b-row>
							<b-col cols="3">
								<b-form-group label="Customer ID" label-for="customer-id">
									<div class="input-with-clear">
										<b-form-input id="customer-id" v-model="filters.customerId" placeholder="Enter Customer ID"></b-form-input>
										<span v-if="filters.customerId" class="clear-icon" @click="clearCustomerId">&times;</span>
									</div>
								</b-form-group>
							</b-col>
							<b-col cols="3">
								<b-form-group label="From Date" label-for="from-date">
									<b-form-datepicker id="from-date" v-model="filters.fromDate"></b-form-datepicker>
									<b-form-timepicker id="from-time" v-model="filters.fromTime"></b-form-timepicker>
								</b-form-group>
							</b-col>
							<b-col cols="3">
								<b-form-group label="To Date" label-for="to-date">
									<b-form-datepicker id="to-date" v-model="filters.toDate"></b-form-datepicker>
									<b-form-timepicker id="to-time" v-model="filters.toTime"></b-form-timepicker>
								</b-form-group>
							</b-col>
							<b-col cols="3" class="d-flex align-items-end">
								<b-button type="submit" variant="primary">Apply Filters</b-button>
							</b-col>
						</b-row>
					</b-form>
				</div>

				<!-- Results area -->
				<div class="results-section">
					<div v-if="messages.length === 0" class="no-results">No messages found.</div>
					<b-table
                        v-else
                        :items="processedMessages"
                        :fields="fields"
                        class="results-table"
                        @row-clicked="onRowClicked"
                        selectable
                    >
                        <template v-slot:cell(body)="data">
                            <span :class="{ 'highlighted-text': data.item.isHighlighted }">{{ data.item.displayBody }}</span>
                            <b-button v-if="data.item.body.length > 500" size="sm" variant="link" @click="toggleMessageTruncation(data.item)">
                                {{ data.item.isTruncated ? '[more]' : '[less]' }}
                            </b-button>
                        </template>
                    </b-table>
				</div>
			</div>
		</div>
	</div>

</div>
</template>

<script>
export default {
	name: 'AIChatMessages',
	data() {
		const now = new Date();
		const startDate = new Date(now);
		startDate.setDate(now.getDate() - 2);

		return {
			isLoading: false,
			filters: {
				customerId: '',
				fromDate: startDate.toISOString().slice(0, 10),
				fromTime: startDate.toISOString().slice(11, 16),
				toDate: now.toISOString().slice(0, 10),
				toTime: now.toISOString().slice(11, 16),
			},
			messages: [],
			fields: [
				{ key: 'customerID', label: 'Customer ID', sortable: true },
				{ key: 'employerName', label: 'Employer Name', thStyle: { width: '200px' }, tdStyle: { width: '200px' } , sortable: true },
				{ key: 'direction', label: 'Direction', thStyle: { width: '80px' }, tdStyle: { width: '80px' } },
				{ key: 'body', label: 'Message', thStyle: { width: '700px' }, tdStyle: { width: '700px' }  },
				{ key: 'createDateTime', label: 'Created Date Time', sortable: true },
			],
            customers: [],
		};
	},
	computed: {
		processedMessages() {
			return this.messages.map(msg => {
				// Mask monetary values in the full message body
				const maskedBody = msg.body && typeof msg.body === 'string'
					? msg.body.replace(/£\d{1,3}(?:,\d{3})*(?:\.\d{2})?/g, '£***')
					: msg.body;

				const isTruncated = msg.isTruncated !== false;
				const truncatedBody = maskedBody && maskedBody.length > 500
					? `${maskedBody.slice(0, 500)}...`
					: maskedBody;

				return {
					...msg,
					displayBody: isTruncated ? truncatedBody : maskedBody,
					isTruncated,
					isHighlighted: msg.body.startsWith("I'd like to talk about"),
				};
			});
		},
	},
	methods: {
        getCustomers() {
            return new Promise((resolve, reject) => {
                this.$http.get(`${this.$config.ApiUri}/Customers`)
                    .then(response => {
                        this.customers = response.data;
                        resolve();
                    })
                    .catch(error => {
                        console.error('Error fetching customers:', error);
                        reject(error);
                    });
            });
        },
        getEmployers() {
            return new Promise((resolve, reject) => {
                this.$http.get(`${this.$config.ApiUri}/Employers`)
                    .then(response => {
                        this.employers = response.data;
                        resolve();
                    })
                    .catch(error => {
                        console.error('Error fetching employers:', error);
                        reject(error);
                    });
            });
        },
		fetchMessages() {
			this.isLoading = true;

			return new Promise((resolve, reject) => {
				// Combine date and time filters
				const fromDateTime = new Date(`${this.filters.fromDate}T${this.filters.fromTime}`).toISOString();
				const toDateTime = new Date(`${this.filters.toDate}T${this.filters.toTime}`).toISOString();

				// Construct query parameters
				const params = new URLSearchParams({
					customerId: this.filters.customerId,
					fromDateTime,
					toDateTime,
				});

				// Fetch data from the endpoint using the provided API call format
				this.$http.get(`${this.$config.ApiUri}/Customers/Chats/Messages?${params.toString()}`)
					.then(response => {
						// Ensure each message has a unique identifier and initialize `isTruncated` when messages are fetched
						this.messages = response.data
							.map(msg => ({
								...msg,
								isTruncated: true, // Initialize `isTruncated` to true for all messages
							}))
							.filter(msg => !/^\b[A-Z]+\b$/.test(msg.body.trim())); // Remove messages with 1 word in all caps
						resolve();
					})
					.catch(error => {
						console.error(error);
						alert('An error occurred while fetching messages.');
						reject(error);
					})
					.finally(() => {
						this.isLoading = false;
					});
			});
		},
        mergeEmployerIntoMessages() {
            this.messages = this.messages.map(msg => {
                const customer = this.customers.find(cust => cust.id === msg.customerID);
                const employerID = customer ? customer.employerID : null;
                const employer = this.employers.find(emp => emp.id === employerID);
                if (employer && employer.name === 'Money Guided') {
                    return null; // Exclude this message
                }
                return { 
                    ...msg, 
                    employerName: employer ? employer.name : 'Unknown' 
                };
            }).filter(msg => msg !== null); // Remove null messages
        },
		initializeData() {
			this.getCustomer()
				.then(() => this.getEmployers())
				.then(() => this.fetchMessages())
                .then(() => this.mergeEmployerIntoMessages())
				.catch(error => {
					console.error('Error during initialization:', error);
				});
		},
		applyFilters() {
			// Combine date and time filters
			const fromDateTime = new Date(`${this.filters.fromDate}T${this.filters.fromTime}`);
			const toDateTime = new Date(`${this.filters.toDate}T${this.filters.toTime}`);

			// Validate that fromDateTime is less than or equal to toDateTime
			if (fromDateTime > toDateTime) {
				alert('The From datetime must be less than or equal to the To datetime.');
				return;
			}

            this.fetchMessages()
                .then(() => this.mergeEmployerIntoMessages())
                .catch(error => {
                    console.error('Error during message fetching:', error);
                });
		},
		clearCustomerId() {
			this.filters.customerId = ''; // Clear the customerId filter
			this.applyFilters(); // Reapply filters
		},
		onRowClicked(row) {
            console.log('row', row);
			if (row && row.customerID !== undefined) {
				this.filters.customerId = row.customerID;
				this.applyFilters();
			} else {
				console.warn('Row clicked but customerId is missing or undefined:', row);
			}
		},
        toggleMessageTruncation(message) {
            const targetMessage = this.messages.find(msg => msg.id === message.id); // Use `id` instead of `body`
            if (targetMessage) {
                this.$set(targetMessage, 'isTruncated', !targetMessage.isTruncated);
            } else {
                console.warn('Message not found for toggling truncation:', message);
            }
        },
	},
	mounted() {
        this.getCustomers()
            .then(() => this.getEmployers())
            .then(() => this.fetchMessages())
            .then(() => this.mergeEmployerIntoMessages())
            .catch(error => {
                console.error('Error during initialization:', error);
            });
	},
};
</script>

<style scoped>
.chatMessages {
	display: flex;
	flex-direction: column;
	height: 100vh;
	background-color: #f8f9fa;
}

.main-container {
	display: flex;
	flex: 1;
	overflow: hidden;
	height: calc(100vh - 56px);
}

.content-panel {
	flex: 1;
	display: flex;
	flex-direction: column;
	background-color: white;
	position: relative;
	overflow: hidden;
}

.content-wrapper {
	display: flex;
	flex-direction: column;
	height: 100%;
	padding: 20px;
	gap: 20px;
	overflow: hidden;
}

.results-section {
	flex: 1;
	overflow: auto;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	background-color: white;
	min-height: 100px;
	display: flex;
	flex-direction: column;
	/* Ensure rows in the results table are not responsive and maintain a fixed height */
	display: block; /* Prevent flex behavior */
}

.results-table {
	margin: 0;
	flex: 1;
	font-size: 0.85rem;
	line-height: 1.2;
	table-layout: fixed; /* Prevent rows from stretching */
	width: 100%;
}

.results-table td,
.results-table th {
	padding: 0.25rem 0.5rem;
	vertical-align: middle;
}

.results-table tbody tr {
	height: 50px; /* Set a fixed height for rows */
	display: table-row; /* Ensure proper table row behavior */
	flex-grow: unset; /* Explicitly unset flex-grow */
}

.loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	background-color: rgba(255, 255, 255, 0.9);
	z-index: 1000;
}

.error-message {
	margin: 20px;
	padding: 16px;
	color: #d32f2f;
	background-color: #ffebee;
	border-radius: 4px;
}

.no-results {
	text-align: center;
	padding: 20px;
	color: #666;
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
}

.alert-fixed {
	position: fixed;
	top: 56px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 1000;
	min-width: 300px;
	text-align: center;
}

.submenu {
	background-color: #f8f9fa;
	border-bottom: 1px solid #dee2e6;
}

.submenu a {
	color: #495057;
	text-decoration: none;
	padding: 0.5rem 1rem;
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
}

.submenu a:hover {
	color: #007bff;
}

.submenu a.disabled {
	color: #6c757d;
	cursor: not-allowed;
	opacity: 0.65;
}

.truncated-cell {
	display: inline-block;
	max-width: 300px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	vertical-align: middle;
}

.results-table td {
	max-width: 200px;
	padding: 0.5rem;
}

.results-summary {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.75rem 1rem;
	background-color: #f8f9fa;
	border-bottom: 1px solid #dee2e6;
	gap: 1rem;
}

.results-count {
	font-size: 0.95rem;
	color: #495057;
	font-weight: 500;
	display: flex;
	align-items: center;
}

.results-count .b-icon {
	color: #6c757d;
}

.input-with-clear {
	position: relative;
}

.input-with-clear .clear-icon {
	position: absolute;
	top: 50%;
	right: 10px;
	transform: translateY(-50%);
	color: red;
	cursor: pointer;
	font-size: 1.2rem;
}

.highlighted-text {
    color: #6c757d; /* Light gray color */
}
</style>